/**
 * Runtime readers for the school-map payload. The API declares its GeoJSON layers as untyped
 * `dict[str, Any]`, so these helpers read only the properties the UI needs, defensively.
 */
import { FINDING_STATUSES, type FindingStatus, type SchoolMap } from "../api/client";

export interface MapFeature {
  geometry: unknown;
  properties: Record<string, unknown>;
}

export interface MapFindingSummary {
  key: string;
  rank: number;
  severity: string;
  title: string;
  location: string;
  status: FindingStatus;
  exposureLow: number;
  exposureHigh: number;
  confidence: string;
}

export type Bounds = [[number, number], [number, number]];

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function str(v: unknown, fallback = ""): string {
  return typeof v === "string" ? v : fallback;
}

function num(v: unknown, fallback = 0): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

export function features(collection: unknown): MapFeature[] {
  if (!isRecord(collection) || !Array.isArray(collection.features)) return [];
  return collection.features.filter(isRecord).map((f) => ({
    geometry: f.geometry,
    properties: isRecord(f.properties) ? f.properties : {},
  }));
}

export function asStatus(v: unknown): FindingStatus {
  return (FINDING_STATUSES as readonly unknown[]).includes(v) ? (v as FindingStatus) : "ny";
}

export function findingSummaries(map: SchoolMap): MapFindingSummary[] {
  return features(map.findings)
    .map(({ properties: p }) => ({
      key: str(p.key),
      rank: num(p.rank),
      severity: str(p.severity),
      title: str(p.title),
      location: str(p.location),
      status: asStatus(p.status),
      exposureLow: num(p.exposure_low),
      exposureHigh: num(p.exposure_high),
      confidence: str(p.confidence, "robust"),
    }))
    .filter((f) => f.key)
    .sort((a, b) => a.rank - b.rank);
}

export function schoolInfo(map: SchoolMap): { name: string; trinn: string } {
  const school: unknown = map.school;
  const p = isRecord(school) && isRecord(school.properties) ? school.properties : {};
  return { name: str(p.name), trinn: str(p.trinn) };
}

/** Return a copy of the payload with one finding's status replaced (after a save). */
export function withFindingStatus(map: SchoolMap, key: string, status: FindingStatus): SchoolMap {
  if (!isRecord(map.findings) || !Array.isArray(map.findings.features)) return map;
  return {
    ...map,
    findings: {
      ...map.findings,
      features: map.findings.features.map((f: unknown) =>
        isRecord(f) && isRecord(f.properties) && f.properties.key === key
          ? { ...f, properties: { ...f.properties, status } }
          : f,
      ),
    },
  };
}

export function maxProperty(collection: unknown, prop: string): number {
  return features(collection).reduce((m, f) => Math.max(m, num(f.properties[prop])), 0);
}

function collectCoords(geometry: unknown, out: [number, number][]): void {
  if (!isRecord(geometry)) return;
  if (geometry.type === "GeometryCollection" && Array.isArray(geometry.geometries)) {
    geometry.geometries.forEach((g: unknown) => collectCoords(g, out));
    return;
  }
  const walk = (c: unknown): void => {
    if (!Array.isArray(c)) return;
    if (c.length >= 2 && typeof c[0] === "number" && typeof c[1] === "number") {
      out.push([c[0], c[1]]);
    } else {
      c.forEach(walk);
    }
  };
  walk(geometry.coordinates);
}

export function geometryBounds(geometry: unknown): Bounds | null {
  const coords: [number, number][] = [];
  collectCoords(geometry, coords);
  if (coords.length === 0) return null;
  let [minX, minY] = coords[0] as [number, number];
  let [maxX, maxY] = [minX, minY];
  for (const [x, y] of coords) {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  return [
    [minX, minY],
    [maxX, maxY],
  ];
}

/** `bbox` from the API is [minLon, minLat, maxLon, maxLat] (may be empty). */
export function bboxBounds(bbox: number[]): Bounds | null {
  const [a, b, c, d] = bbox;
  if (a === undefined || b === undefined || c === undefined || d === undefined) return null;
  return [
    [a, b],
    [c, d],
  ];
}

export function findingGeometry(map: SchoolMap, key: string): unknown {
  return features(map.findings).find((f) => f.properties.key === key)?.geometry;
}
